import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { ethers } from 'ethers';
import EthereumProvider from '@walletconnect/ethereum-provider';
import {
  setWalletConnectSession,
  clearWalletConnectSession,
  getActiveEip1193Provider,
} from '../../utils/activeEip1193Provider';
import { getWalletConnectProjectId } from '../../utils/walletConnectEnv';

function expectedChainId() {
  return Number(process.env.REACT_APP_CHAIN_ID || 8453);
}

// 비동기 액션들
export const connectWallet = createAsyncThunk(
  'wallet/connect',
  async (arg = {}, { rejectWithValue }) => {
    const mode = arg && arg.mode === 'walletconnect' ? 'walletconnect' : 'injected';
    try {
      if (mode === 'injected') {
        if (!window.ethereum) {
          throw new Error('브라우저 지갑(MetaMask 등)이 감지되지 않았습니다');
        }

        const accounts = await window.ethereum.request({
          method: 'eth_requestAccounts',
        });

        if (!accounts || accounts.length === 0) {
          throw new Error('지갑 연결이 거부되었습니다');
        }

        const address = accounts[0];
        const provider = new ethers.providers.Web3Provider(window.ethereum);
        const network = await provider.getNetwork();
        const balance = await provider.getBalance(address);
        const balanceInEth = ethers.utils.formatEther(balance);

        return {
          address,
          balance: balanceInEth,
          network: network.name,
          chainId: network.chainId,
          connectionMode: 'injected',
        };
      }

      const projectId = getWalletConnectProjectId();
      if (!projectId) {
        throw new Error(
          'WalletConnect 를 쓰려면 REACT_APP_WALLETCONNECT_PROJECT_ID 를 설정하세요 (WalletConnect Cloud)'
        );
      }

      await clearWalletConnectSession();

      const chainIdNum = expectedChainId();
      const wc = await EthereumProvider.init({
        projectId,
        chains: [chainIdNum],
        optionalChains: [chainIdNum],
        showQrModal: true,
        metadata: {
          name: 'PingPing',
          description: 'USDC escrow · claim',
          url: typeof window !== 'undefined' ? window.location.origin : 'https://pingping.local',
          icons:
            typeof window !== 'undefined'
              ? [`${window.location.origin}/logo192.png`]
              : [],
        },
      });

      await wc.enable();
      setWalletConnectSession(wc);

      const ethersProvider = new ethers.providers.Web3Provider(wc);
      const signer = ethersProvider.getSigner();
      const address = await signer.getAddress();
      const network = await ethersProvider.getNetwork();
      const balance = await ethersProvider.getBalance(address);
      const balanceInEth = ethers.utils.formatEther(balance);

      return {
        address,
        balance: balanceInEth,
        network: network.name,
        chainId: network.chainId,
        connectionMode: 'walletconnect',
      };
    } catch (error) {
      if (mode === 'walletconnect') {
        await clearWalletConnectSession();
      }
      return rejectWithValue(error.message || String(error));
    }
  }
);

export const disconnectWallet = createAsyncThunk(
  'wallet/disconnect',
  async (arg = {}, { getState }) => {
    const silent = Boolean(arg && arg.silent);
    const { connectionMode } = getState().wallet;
    if (connectionMode === 'walletconnect') {
      await clearWalletConnectSession();
    }
    return { silent };
  }
);

/** 연결 유지 중 체인·계정·잔액만 동기화 (로딩 표시 없음) */
export const refreshWalletSnapshot = createAsyncThunk(
  'wallet/refreshSnapshot',
  async (_, { getState, rejectWithValue }) => {
    try {
      const { isConnected, connectionMode } = getState().wallet;
      if (!isConnected) {
        return rejectWithValue('not_connected');
      }
      const eip1193 = getActiveEip1193Provider(connectionMode);
      if (!eip1193) {
        return rejectWithValue('no_provider');
      }
      const provider = new ethers.providers.Web3Provider(eip1193);
      const network = await provider.getNetwork();
      const signer = provider.getSigner();
      const address = await signer.getAddress();
      const balance = await provider.getBalance(address);
      return {
        address,
        chainId: network.chainId,
        network: network.name,
        balance: ethers.utils.formatEther(balance),
      };
    } catch (error) {
      return rejectWithValue(error.message || String(error));
    }
  },
  {
    condition: (_, { getState }) => Boolean(getState().wallet.isConnected),
  }
);

export const updateBalance = createAsyncThunk(
  'wallet/updateBalance',
  async (address, { getState, rejectWithValue }) => {
    try {
      const { connectionMode } = getState().wallet;
      const eip1193 = getActiveEip1193Provider(connectionMode);
      if (!eip1193) {
        throw new Error('지갑 프로바이더가 없습니다');
      }

      const provider = new ethers.providers.Web3Provider(eip1193);
      const balance = await provider.getBalance(address);
      const balanceInEth = ethers.utils.formatEther(balance);

      return balanceInEth;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const sendTransaction = createAsyncThunk(
  'wallet/sendTransaction',
  async ({ to, amount }, { getState, rejectWithValue }) => {
    try {
      const { connectionMode } = getState().wallet;
      const eip1193 = getActiveEip1193Provider(connectionMode);
      if (!eip1193) {
        throw new Error('지갑 프로바이더가 없습니다');
      }

      const provider = new ethers.providers.Web3Provider(eip1193);
      const signer = provider.getSigner();

      const tx = await signer.sendTransaction({
        to,
        value: ethers.utils.parseEther(amount.toString()),
      });

      const receipt = await tx.wait();

      return {
        hash: tx.hash,
        receipt,
        amount,
        to,
      };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// 초기 상태
const initialState = {
  isConnected: false,
  address: null,
  balance: '0',
  network: null,
  chainId: null,
  /** @type {'injected' | 'walletconnect' | null} */
  connectionMode: null,
  loading: false,
  error: null,
  transactions: [],
};

// 슬라이스 생성
const walletSlice = createSlice({
  name: 'wallet',
  initialState,
  reducers: {
    setWalletConnected: (state, action) => {
      state.isConnected = action.payload;
    },

    setWalletAddress: (state, action) => {
      state.address = action.payload;
    },

    setBalance: (state, action) => {
      state.balance = action.payload;
    },

    setNetwork: (state, action) => {
      state.network = action.payload;
    },

    clearError: (state) => {
      state.error = null;
    },

    addTransaction: (state, action) => {
      state.transactions.unshift(action.payload);
    },

    updateTransaction: (state, action) => {
      const { hash, status } = action.payload;
      const transaction = state.transactions.find((tx) => tx.hash === hash);
      if (transaction) {
        transaction.status = status;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(connectWallet.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(connectWallet.fulfilled, (state, action) => {
        state.loading = false;
        state.isConnected = true;
        state.address = action.payload.address;
        state.balance = action.payload.balance;
        state.network = action.payload.network;
        state.chainId = action.payload.chainId;
        state.connectionMode = action.payload.connectionMode;
      })
      .addCase(connectWallet.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(disconnectWallet.pending, (state, action) => {
        if (!action.meta.arg?.silent) {
          state.loading = true;
        }
      })
      .addCase(disconnectWallet.fulfilled, (state) => {
        state.loading = false;
        state.isConnected = false;
        state.address = null;
        state.balance = '0';
        state.network = null;
        state.chainId = null;
        state.connectionMode = null;
      })
      .addCase(disconnectWallet.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(updateBalance.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateBalance.fulfilled, (state, action) => {
        state.loading = false;
        state.balance = action.payload;
      })
      .addCase(updateBalance.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(sendTransaction.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(sendTransaction.fulfilled, (state, action) => {
        state.loading = false;
        state.transactions.unshift({
          hash: action.payload.hash,
          amount: action.payload.amount,
          to: action.payload.to,
          status: 'confirmed',
          timestamp: new Date().toISOString(),
        });
      })
      .addCase(sendTransaction.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(refreshWalletSnapshot.fulfilled, (state, action) => {
        state.address = action.payload.address;
        state.chainId = action.payload.chainId;
        state.network = action.payload.network;
        state.balance = action.payload.balance;
      });
  },
});

export const {
  setWalletConnected,
  setWalletAddress,
  setBalance,
  setNetwork,
  clearError,
  addTransaction,
  updateTransaction,
} = walletSlice.actions;

export const selectWallet = (state) => state.wallet;
export const selectIsWalletConnected = (state) => state.wallet.isConnected;
export const selectWalletAddress = (state) => state.wallet.address;
export const selectWalletBalance = (state) => state.wallet.balance;
export const selectWalletNetwork = (state) => state.wallet.network;
export const selectWalletChainId = (state) => state.wallet.chainId;
export const selectWalletConnectionMode = (state) => state.wallet.connectionMode;
export const selectWalletLoading = (state) => state.wallet.loading;
export const selectWalletError = (state) => state.wallet.error;
export const selectTransactions = (state) => state.wallet.transactions;

export default walletSlice.reducer;
